import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';
import {
  Grid,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider
} from '@mui/material';
import theme from 'src/theme';
import Loader from 'src/common/Loader';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setSuccessInfo, setErrorInfo } from 'src/redux/modalSlice';
import { addNewVehicleRecord, getAllVehicles } from 'src/redux/vehicleSlice';
import { getShiftDetails } from 'src/redux/settingSlice';
import InputField from 'src/common/formFields/InputField';
import FMSModal from 'src/common/FMSModal';
import SelectField from 'src/common/formFields/SelectField';
import AutocompleteField from 'src/common/formFields/AutocompleteField';
import DatePickerField from 'src/common/formFields/DatePickerField';
import {
  vehicleRecordFormModel,
  vehicleRecordFormInitialValues,
  vehicleRecordValidationSchema
} from './vehicleRecordFormModel';

const NewRecord = ({ open, handleClose, initialValues }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const { allVehicles } = useSelector((state) => state.vehicles);
  const { shiftDetails, shiftLoaded } = useSelector((state) => state.settings);
  const formInitValues = initialValues || vehicleRecordFormInitialValues;
  const {
    formId,
    formField: {
      vehicleNumber,
      shift,
      openingReading,
      closingReading,
      runningHour,
      trips,
      quantity,
      hsd,
      recordDate
    }
  } = vehicleRecordFormModel;

  useEffect(() => {
    if (open) {
      dispatch(getAllVehicles());
      if (!shiftLoaded) {
        dispatch(getShiftDetails());
      }
    }
  }, [dispatch, open, shiftLoaded]);

  const onSubmit = async (values, actions) => {
    console.log('form values', values);
    setLoading(true);
    try {
      await dispatch(addNewVehicleRecord(values)).unwrap();
      handleClose();
      setLoading(false);
      dispatch(
        setSuccessInfo({
          successMsg: 'vehicle record has been created successfully!'
        })
      );
      // handle result here
    } catch (error) {
      console.log('error', error);
      // handle error here
      actions.setSubmitting(false);
      setLoading(false);
      dispatch(setErrorInfo({ errorMsg: error.error_message }));
    }
  };

  return (
    <FMSModal open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          fontSize: 18,
          background: theme.palette.grey[800],
          color: theme.palette.common.white
        }}
      >
        {initialValues ? 'UPDATE VEHICLE RECORD' : 'ADD NEW VEHICLE RECORD'}
      </DialogTitle>
      <Divider />
      <Formik
        initialValues={formInitValues}
        validationSchema={vehicleRecordValidationSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, errors }) => {
          console.log('errors', errors);

          return (
            <>
              {loading ? (
                <Loader />
              ) : (
                <Form id={formId}>
                  <DialogContent>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <AutocompleteField
                          name={vehicleNumber.name}
                          label={vehicleNumber.label}
                          options={allVehicles}
                          getOptionLabel={(option) => option.registration_num}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <SelectField
                          name={shift.name}
                          label={shift.label}
                          data={shiftDetails}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputField
                          name={openingReading.name}
                          label={openingReading.label}
                          upperCase
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputField
                          name={closingReading.name}
                          label={closingReading.label}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputField
                          name={runningHour.name}
                          label={runningHour.label}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputField
                          name={trips.name}
                          label={trips.label}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputField
                          name={quantity.name}
                          label={quantity.label}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <InputField
                          name={hsd.name}
                          label={hsd.label}
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <DatePickerField
                          name={recordDate.name}
                          label={recordDate.label}
                          onlyDate
                          disableFuture
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                  </DialogContent>
                  <DialogActions sx={{ p: 2 }}>
                    <Button variant="outlined" size="large" onClick={handleClose}>
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      disabled={isSubmitting}
                      type="submit"
                      size="large"
                      variant="contained"
                    >
                      {initialValues ? 'Update Record' : 'Add Record'}
                    </Button>
                  </DialogActions>
                </Form>
              )}
            </>
          );
        }}
      </Formik>
    </FMSModal>
  );
};

NewRecord.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialValues: PropTypes.object
};

export default NewRecord;
